/* eslint-disable arrow-body-style */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTranslation } from 'react-i18next'
import { TranslationItemModel } from '@/shared/api/BaseAPI.model'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useDishSizeFormConfig } from '@/features/menu-management/SidebarAccordion/forms/useDishSizeFormConfig'
import AccordionContent from '../AccordionContent/AccordionContent'
import { QUERY } from '../../api/SidebarAccordionAPI.model'
import SidebarAccordionAPI from '../../api/SidebarAccordionAPI'
import { useDishSizesQuery } from '../../hooks/useDishSizesQuery'
import { useSelectedLanguagesQuery } from '../../hooks/useSelectedLanguagesQuery'
import { useFormDirtyStore } from '@/shared/store/useFormDirtyStore'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'

interface DishSizesAccordionProps {
  accordionIndex: number
  openAccordion: number
  handleAccordionClick: (index: number) => void
}

function DishSizesAccordion(props: DishSizesAccordionProps) {
  const { accordionIndex, openAccordion, handleAccordionClick } = props
  const { t } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const initialDishSizeFormConfig = useDishSizeFormConfig()
  const [updatedDishSizeFormConfig, setUpdatedDishSizeFormConfig] = useState(
    initialDishSizeFormConfig
  )
  const currentLanguage =
    useTranslation().i18n.language || localStorage.getItem('i18nextLng') || ''
  const { selectedLanguagesList } = useSelectedLanguagesQuery(currentLanguage)
  const [dishSizeId, setDishSizeId] = useState<string>()
  const configuredDishSizeIdRef = useRef<string>()
  const resetIsDirty = useFormDirtyStore(state => state.reset)
  const { showSnackbar } = useSnackbar()

  const dishSize = useQuery({
    queryKey: [QUERY.DISH_SIZE, dishSizeId],
    queryFn: () => SidebarAccordionAPI.getDishSize(dishSizeId!),
    enabled: !!dishSizeId
  })

  const { dishSizesList, postDishSize, putDishSize, deleteDishSize } =
    useDishSizesQuery()

  const contentList = dishSizesList?.data?.map(item => {
    const updatedTranslations = selectedLanguagesList?.map(language => {
      const existingTranslation = item.translations.find(
        translation => translation.languageCode === language.languageCode
      )
      return (
        existingTranslation || {
          name: t('SIDEBAR_ACCORDION.MISSING_TRANSLATION'),
          languageCode: language.languageCode
        }
      )
    })
    return {
      ...item,
      translations: updatedTranslations || []
    }
  })

  // The modal opens only from inside the effect above, on data. A failed load
  // therefore reads as a dead button, nothing opens and nothing is said, and
  // the row cannot even be retried, because `dishSizeId` stays set and clicking
  // the same row again is not a state change.
  useEffect(() => {
    if (!dishSize.isError) return
    showSnackbar(
      t('ALERTS.LOAD_FOR_EDIT_ERROR.TITLE'),
      t('ALERTS.LOAD_FOR_EDIT_ERROR.DESCRIPTION'),
      'error'
    )
    configuredDishSizeIdRef.current = undefined
    setDishSizeId(undefined)
  }, [dishSize.isError, showSnackbar, t])

  // Declared ahead of the submit handlers that call it, so it can stay an
  // arrow constant instead of a hoisted declaration (react/jsx-no-bind).
  const handleOnCloseModal = () => {
    configuredDishSizeIdRef.current = undefined
    setDishSizeId(undefined)
    setIsModalOpen(false)
  }

  const handleAddDishSize = () => {
    const onSubmit = (data: any) => {
      const translations = data.translations || {}

      const payload: { translations: TranslationItemModel[] } = {
        translations:
          selectedLanguagesList?.map(language => ({
            name: translations[language.languageCode] || '',
            languageCode: language.languageCode
          })) || []
      }
      // Closed from the mutation's own success callback so a failed save
      // keeps the form and everything typed into it. Closing here skips
      // AccordionItemModal's close path, the only place that clears the dirty
      // flag, so that is done here too.
      postDishSize(payload, {
        onSuccess: () => {
          resetIsDirty()
          handleOnCloseModal()
        }
      })
    }
    const newFormConfig = {
      ...initialDishSizeFormConfig,
      // Explicitly empty: spreading the initial config alone would leave the
      // `defaultValues` of the size edited before it in place, and the add
      // form would open pre-filled with that size.
      defaultValues: undefined,
      onSubmit
    }
    setUpdatedDishSizeFormConfig(newFormConfig)
    setIsModalOpen(true)
  }

  const hadleEditDishSize = (menuItemId: string) => {
    setDishSizeId(menuItemId)
  }

  useEffect(() => {
    // Keyed on the id rather than on `dishSize.data`: react-query's structural
    // sharing hands back the same object when nothing changed, so an effect
    // watching only the data can miss a reopen.
    if (
      dishSize.data &&
      dishSizeId &&
      dishSizeId !== configuredDishSizeIdRef.current
    ) {
      configuredDishSizeIdRef.current = dishSizeId
      const newOnSubmit = (data: any) => {
        const languageCodeList: string[] = []
        languageCodeList.push(...Object.keys(data.translations))

        const translationsList: string[] = []
        translationsList.push(...(Object.values(data.translations) as string[]))

        const payload: { translations: TranslationItemModel[] } = {
          translations: translationsList.map(
            (translation: string, index): TranslationItemModel => ({
              name: translation,
              languageCode: languageCodeList[index]
            })
          )
        }
        putDishSize(
          { dishSizeId, dishSizePayload: payload },
          {
            onSuccess: () => {
              resetIsDirty()
              handleOnCloseModal()
            }
          }
        )
      }

      const selectedLanguageListCodeNames = selectedLanguagesList?.map(
        language => language.languageCode
      )

      const dishSizeLanguageList = dishSize.data.translations.filter(
        translation =>
          selectedLanguageListCodeNames?.includes(translation.languageCode)
      )

      const defaultValues = {
        translations: {
          ...dishSizeLanguageList?.reduce((acc: any, translation) => {
            acc[translation.languageCode] = translation.name
            return acc
          }, {})
        }
      }

      const newFormConfig = {
        ...initialDishSizeFormConfig,
        defaultValues,
        onSubmit: newOnSubmit
      }
      setUpdatedDishSizeFormConfig(newFormConfig)
      setIsModalOpen(true)
    }
  }, [dishSize.data, dishSizeId, resetIsDirty])

  return (
    <AccordionContent
      title={t('SIDEBAR_ACCORDION.SIZES.TITLE')}
      modalTitle={t('SIDEBAR_ACCORDION.SIZES.MODAL_TITLE')}
      accordionIndex={accordionIndex}
      openAccordion={openAccordion}
      handleAccordionClick={handleAccordionClick}
      contentList={contentList || []}
      deleteFunction={deleteDishSize}
      deleteMessage={t('SIDEBAR_ACCORDION.SIZES.DELETE_MESSAGE')}
      loadingItemId={dishSize.isLoading ? dishSizeId : undefined}
      accordionItemFormConfig={updatedDishSizeFormConfig}
      handleAddAccordionItem={handleAddDishSize}
      handleEditAccordionItem={hadleEditDishSize}
      handleOnCloseModal={handleOnCloseModal}
      isModalOpen={isModalOpen}
    />
  )
}

export default DishSizesAccordion
